import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Compass, Edit3, TrendingDown, X, ArrowLeft, Users, ClipboardList } from 'lucide-react';
import { storeConstraintContext, clearConstraintContext } from '../../lib/constraintContext';

/**
 * SolutionFinderContextStack
 * ─────────────────────────────────────────────────────────────────
 * Consolidates the four narrative-bridge banners that previously
 * lived inline on SolutionFinderPage:
 *   1. business_case   — round-trip from a saved Business Case
 *   2. constraint      — arrival from Mission Control / Impact Modelling
 *   3. nav_condition   — arrival from Org Benchmarks "What Top Navigators Do Differently"
 *   4. industry_kpi    — arrival from Industry Benchmarks Navigation Advantage card
 *
 * Each banner shares one visual rhythm (rounded-xl border, 12px gap,
 * left icon disc, right dismiss). Stack order = priority: a saved
 * business-case round-trip wins because it carries an escape hatch,
 * then upstream narrative context, then descending granularity.
 *
 * Dismissals persist for the browser session via sessionStorage keyed
 * by `${type}:${contentHash}` so the same banner doesn't reappear
 * after the user dismisses it. New params or a new case re-surface.
 */

const DISMISS_KEY_PREFIX = 'sf-context-stack-dismissed';

function hashContent(parts) {
  // Lightweight stable hash for sessionStorage keys. Not crypto —
  // just enough to distinguish a "+9 pp Coordination" banner from
  // a "+8 pp Capability" one.
  return parts
    .filter((p) => p !== undefined && p !== null && p !== '')
    .join('|');
}

function isDismissed(type, contentHash) {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(`${DISMISS_KEY_PREFIX}:${type}:${contentHash}`) === '1';
  } catch {
    return false;
  }
}

function markDismissed(type, contentHash) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(`${DISMISS_KEY_PREFIX}:${type}:${contentHash}`, '1');
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
}

const TONE_CLASSES = {
  teal: {
    border: 'border-teal-200',
    bg: 'bg-gradient-to-r from-teal-50 to-blue-50',
    iconWrap: 'bg-teal-100 text-teal-700',
    eyebrow: 'text-teal-800',
  },
  orange: {
    border: 'border-orange-200',
    bg: 'bg-gradient-to-r from-amber-50 to-orange-50/70',
    iconWrap: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200',
    eyebrow: 'text-orange-700',
  },
  emerald: {
    border: 'border-teal-300',
    bg: 'bg-gradient-to-r from-teal-50 to-emerald-50',
    iconWrap: 'bg-teal-100 text-teal-700',
    eyebrow: 'text-teal-700',
  },
  indigo: {
    border: 'border-indigo-200',
    bg: 'bg-gradient-to-r from-indigo-50 to-violet-50/70',
    iconWrap: 'bg-indigo-100 text-indigo-700',
    eyebrow: 'text-indigo-700',
  },
  violet: {
    border: 'border-violet-200',
    bg: 'bg-gradient-to-r from-violet-50 to-fuchsia-50/60',
    iconWrap: 'bg-violet-100 text-violet-700',
    eyebrow: 'text-violet-700',
  },
  cyan: {
    border: 'border-cyan-200',
    bg: 'bg-gradient-to-r from-cyan-50 to-blue-50/70',
    iconWrap: 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200',
    eyebrow: 'text-cyan-700',
  },
  rose: {
    border: 'border-rose-200',
    bg: 'bg-gradient-to-r from-rose-50 to-orange-50/60',
    iconWrap: 'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
    eyebrow: 'text-rose-700',
  },
};

/**
 * Single banner row — used by every entry in the stack so visual
 * rhythm stays identical. `actions` slot lets the business-case
 * banner inject its "Back to case" / "Discard" buttons in place of
 * the standard dismiss X.
 */
function ContextStackItem({
  tone = 'teal',
  icon: Icon,
  eyebrow,
  headline,
  description,
  testId,
  dismissTestId,
  dataAttrs,
  actions,
  onDismiss,
}) {
  const palette = TONE_CLASSES[tone] || TONE_CLASSES.teal;
  return (
    <div
      className={`rounded-xl border ${palette.border} ${palette.bg} px-4 py-3 flex items-start gap-3`}
      data-testid={testId}
      {...(dataAttrs || {})}
    >
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${palette.iconWrap}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p
          className={`text-[10px] font-bold uppercase tracking-[0.14em] ${palette.eyebrow}`}
        >
          {eyebrow}
        </p>
        <div className="text-sm font-semibold text-slate-900 leading-snug mt-0.5">
          {headline}
        </div>
        {description && (
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      {actions ? (
        <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
      ) : (
        <button
          type="button"
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
          aria-label="Dismiss"
          data-testid={dismissTestId}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function SolutionFinderContextStack({
  prefillContext,
  onBackToCase,
  onDiscardPrefill,
  kpis,
  // hint kept for backward-compat with the old constraint banner
  constraintHint,
}) {
  const [searchParams] = useSearchParams();
  // Tick state to force re-render on dismiss — sessionStorage doesn't
  // emit React updates by itself.
  const [, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);

  // ─── Source 1: Mission Control / Impact Modelling constraint ────
  const constraint = useMemo(() => {
    const label = searchParams.get('constraint_label');
    const leak = parseInt(searchParams.get('leak'), 10);
    if (!label || !Number.isFinite(leak)) return null;
    const recovery = parseInt(searchParams.get('recovery'), 10);
    return {
      stage: searchParams.get('constraint_stage'),
      label,
      leak,
      recovery: Number.isFinite(recovery) ? recovery : null,
      attenuated: searchParams.get('attenuated') === '1',
    };
  }, [searchParams]);
  useEffect(() => {
    if (constraint) {
      storeConstraintContext({
        stage: constraint.stage,
        label: constraint.label,
        leak: constraint.leak,
        recovery: constraint.recovery,
        attenuated: constraint.attenuated,
      });
    }
  }, [constraint]);

  // ─── Source 2: Org Benchmarks navigation-condition delta ────────
  const navCondition = useMemo(() => {
    const label = searchParams.get('label');
    const focus = searchParams.get('focus');
    const gap = searchParams.get('gap');
    if (!label || !focus) return null;
    return { label, focus, gap: gap ? Number(gap) : null };
  }, [searchParams]);

  // ─── Source 3a: PE Benchmarking workforce-condition gap ─────────
  // Same ?kpi= deep-link as Industry Benchmarks, but flagged with a
  // ?pe_condition= marker. Takes precedence over the industry-KPI
  // banner so the framing matches where the user actually came from.
  const peBenchmarking = useMemo(() => {
    const condition = searchParams.get('pe_condition');
    if (!condition) return null;
    const kpiParam = searchParams.get('kpi');
    const match = (kpis || []).find((k) => k.id === kpiParam);
    return { condition, kpiId: kpiParam || null, kpiName: match?.name || kpiParam || null };
  }, [searchParams, kpis]);

  // ─── Source 3b: Capability Inventory skill/succession gap ───────
  // Arrival from the Capability Inventory (succession bench, what-if
  // backfill, or a member's uncovered role) carrying a ?cap_focus=
  // marker. Uses the ?kpi=mobility bridge (Internal Mobility /
  // Retention) so SF pre-selects an actionable KPI. Suppresses the
  // industry-KPI banner so the framing matches where the user came from.
  const capabilityGap = useMemo(() => {
    const focus = searchParams.get('cap_focus');
    if (!focus) return null;
    const role = searchParams.get('cap_role');
    const gapRaw = parseInt(searchParams.get('cap_gap'), 10);
    return {
      focus,
      role: role || null,
      gap: Number.isFinite(gapRaw) ? gapRaw : null,
    };
  }, [searchParams]);

  // ─── Source 3c: KPI Alignment transfer gap (?align_gap=1) ───────
  // Arrival from the Correlation & Divergence page's KPI Alignment tab
  // "Model the fix" CTA. The KPI is pre-selected via ?kpi=; this marker
  // frames the flow as closing a diagnosed transfer gap and suppresses
  // the generic industry-KPI banner.
  const alignmentGap = useMemo(() => {
    if (!searchParams.get('align_gap')) return null;
    const measureLabel = searchParams.get('align_label');
    const kpiLabel = searchParams.get('align_kpi_label');
    const kpiParam = searchParams.get('kpi');
    const match = (kpis || []).find((k) => k.id === kpiParam);
    const expected = parseInt(searchParams.get('align_expected'), 10);
    const observed = parseInt(searchParams.get('align_observed'), 10);
    return {
      measureLabel: measureLabel || null,
      kpiId: kpiParam || null,
      kpiName: match?.name || kpiLabel || kpiParam || null,
      expected: Number.isFinite(expected) ? expected : null,
      observed: Number.isFinite(observed) ? observed : null,
    };
  }, [searchParams, kpis]);

  // ─── Source 3: Industry Benchmark KPI gap (?kpi=<id>) ───────────
  // We surface a banner only when the kpi maps to a real KPI in the
  // catalog AND the user has at least one Industry Benchmark
  // percentile context attached (no point shouting "you're behind"
  // if we don't know what they're behind on). For v1, presence of
  // the ?kpi param + matching KPI is enough — the data depth check
  // can come later. Suppressed when a PE Benchmarking or Capability
  // Inventory marker is set.
  const industryKpi = useMemo(() => {
    if (searchParams.get('pe_condition') || searchParams.get('cap_focus') || searchParams.get('align_gap')) return null;
    const kpiParam = searchParams.get('kpi');
    if (!kpiParam) return null;
    const match = (kpis || []).find((k) => k.id === kpiParam);
    if (!match) return null;
    return { kpiId: match.id, kpiName: match.name };
  }, [searchParams, kpis]);

  // ─── Compute dismiss state ──────────────────────────────────────
  const constraintHash = constraint
    ? hashContent([constraint.label, constraint.leak, constraint.recovery])
    : '';
  const navConditionHash = navCondition
    ? hashContent([navCondition.focus, navCondition.gap])
    : '';
  const industryKpiHash = industryKpi ? hashContent([industryKpi.kpiId]) : '';
  const peBenchmarkingHash = peBenchmarking ? hashContent([peBenchmarking.condition, peBenchmarking.kpiId]) : '';
  const capabilityGapHash = capabilityGap ? hashContent([capabilityGap.focus, capabilityGap.role]) : '';
  const alignmentGapHash = alignmentGap ? hashContent([alignmentGap.measureLabel, alignmentGap.kpiId]) : '';

  const constraintDismissed = constraint && isDismissed('constraint', constraintHash);
  const navConditionDismissed =
    navCondition && isDismissed('nav_condition', navConditionHash);
  const industryKpiDismissed =
    industryKpi && isDismissed('industry_kpi', industryKpiHash);
  const peBenchmarkingDismissed =
    peBenchmarking && isDismissed('pe_benchmarking', peBenchmarkingHash);
  const capabilityGapDismissed =
    capabilityGap && isDismissed('capability_gap', capabilityGapHash);
  const alignmentGapDismissed =
    alignmentGap && isDismissed('alignment_gap', alignmentGapHash);

  // ─── Render in priority order ───────────────────────────────────
  // Business-case prefill is highest priority because it changes
  // the *write* semantics of the page (saves overwrite the case).
  // Constraint comes next as the broadest upstream framing, then
  // descending granularity: nav-condition (org-level) → industry-kpi
  // (single-metric).
  const items = [];

  if (prefillContext?.caseId) {
    items.push(
      <ContextStackItem
        key="prefill"
        tone="teal"
        icon={Edit3}
        eyebrow="Editing inputs for · business case"
        headline={prefillContext.caseTitle || 'Saved business case'}
        description="Saves from this flow update the existing case in place. Discard the round-trip to start a fresh case instead."
        testId="sf-editing-from-case-banner"
        actions={
          <>
            <button
              onClick={onBackToCase}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-white rounded"
              data-testid="sf-back-to-case-btn"
              type="button"
            >
              <ArrowLeft size={12} /> Back to case
            </button>
            <button
              onClick={onDiscardPrefill}
              className="text-slate-400 hover:text-slate-600"
              title="Discard round-trip — recalculations will create a new case instead of updating"
              data-testid="sf-discard-prefill-btn"
              type="button"
            >
              <X size={14} />
            </button>
          </>
        }
      />,
    );
  }

  if (constraint && !constraintDismissed) {
    items.push(
      <ContextStackItem
        key="constraint"
        tone="orange"
        icon={AlertTriangle}
        eyebrow="From Mission Control · current highest opportunity"
        headline={
          <>
            {constraint.label} is reducing value transfer by {constraint.leak}%.
            {Number.isFinite(constraint.recovery) && (
              <span className="text-emerald-700">
                {' '}Target recovery: {constraint.attenuated ? `+${constraint.recovery}%` : `up to +${constraint.recovery}%`}.
              </span>
            )}
          </>
        }
        description={
          constraintHint ||
          "Pick the business outcome below that this constraint is hurting most — we'll model interventions to recover the leak."
        }
        testId="constraint-context-banner"
        dismissTestId="constraint-context-dismiss"
        onDismiss={() => {
          clearConstraintContext();
          markDismissed('constraint', constraintHash);
          bump();
        }}
      />,
    );
  }

  if (navCondition && !navConditionDismissed) {
    items.push(
      <ContextStackItem
        key="nav-condition"
        tone="emerald"
        icon={Compass}
        eyebrow="Navigation condition gap · from Org Benchmarks"
        headline={
          navCondition.gap !== null && Number.isFinite(navCondition.gap)
            ? `Closing the +${navCondition.gap} pp gap top performers hold on ${navCondition.label}`
            : `Closing the ${navCondition.label} gap`
        }
        description="Top-quartile peers outperform the cohort median on this condition. Pick the KPI below that maps best — Solution Finder will model the interventions that recover the leak."
        testId="sf-nav-condition-banner"
        dismissTestId="sf-nav-condition-banner-dismiss"
        dataAttrs={{ 'data-nav-focus': navCondition.focus }}
        onDismiss={() => {
          markDismissed('nav_condition', navConditionHash);
          bump();
        }}
      />,
    );
  }

  if (peBenchmarking && !peBenchmarkingDismissed) {
    items.push(
      <ContextStackItem
        key="pe-benchmarking"
        tone="violet"
        icon={Users}
        eyebrow="Workforce condition gap · from PE Benchmarking"
        headline={`Strengthening ${peBenchmarking.condition} — the workforce condition most constraining value transfer`}
        description={
          peBenchmarking.kpiName
            ? `We pre-selected ${peBenchmarking.kpiName} as the closest downstream KPI — review the suggested interventions and model how strengthening this condition lifts performance.`
            : 'Review the suggested interventions and model how strengthening this condition lifts performance.'
        }
        testId="sf-pe-benchmarking-banner"
        dismissTestId="sf-pe-benchmarking-banner-dismiss"
        dataAttrs={{ 'data-pe-condition': peBenchmarking.condition }}
        onDismiss={() => {
          markDismissed('pe_benchmarking', peBenchmarkingHash);
          bump();
        }}
      />,
    );
  }

  if (capabilityGap && !capabilityGapDismissed) {
    const headline = capabilityGap.role
      ? `Closing the capability gap for ${capabilityGap.focus} → ${capabilityGap.role}`
      : `Closing the capability gap for ${capabilityGap.focus}`;
    items.push(
      <ContextStackItem
        key="capability-gap"
        tone="cyan"
        icon={ClipboardList}
        eyebrow="Capability gap · from Capability Inventory"
        headline={headline}
        description={
          capabilityGap.gap
            ? `${capabilityGap.focus} is short ${capabilityGap.gap} skill${capabilityGap.gap !== 1 ? 's' : ''} for this role. We pre-selected Internal Mobility & Retention as the closest KPI — model the L&D interventions that build this bench and reduce succession risk.`
            : 'We pre-selected Internal Mobility & Retention as the closest KPI — model the L&D interventions that build this bench and reduce succession risk.'
        }
        testId="sf-capability-gap-banner"
        dismissTestId="sf-capability-gap-banner-dismiss"
        dataAttrs={{ 'data-cap-focus': capabilityGap.focus }}
        onDismiss={() => {
          markDismissed('capability_gap', capabilityGapHash);
          bump();
        }}
      />,
    );
  }

  if (alignmentGap && !alignmentGapDismissed) {
    items.push(
      <ContextStackItem
        key="alignment-gap"
        tone="rose"
        icon={TrendingDown}
        eyebrow="Transfer gap · from KPI Alignment"
        headline={`Converting ${alignmentGap.measureLabel || 'this learning measure'} into ${alignmentGap.kpiName || 'the KPI'}`}
        description={
          alignmentGap.expected !== null && alignmentGap.observed !== null
            ? `${alignmentGap.measureLabel || 'This measure'} is modelled as a strong lever for ${alignmentGap.kpiName || 'this KPI'} (${alignmentGap.expected}% expected influence) but your data shows only ${alignmentGap.observed}% realised — learning isn't transferring yet. The KPI is pre-selected; pick the interventions below that strengthen how this learning converts into results.`
            : `The KPI is pre-selected — pick the interventions below that strengthen how this learning transfers into results.`
        }
        testId="sf-alignment-gap-banner"
        dismissTestId="sf-alignment-gap-banner-dismiss"
        dataAttrs={{ 'data-align-kpi': alignmentGap.kpiId }}
        onDismiss={() => {
          markDismissed('alignment_gap', alignmentGapHash);
          bump();
        }}
      />,
    );
  }

  if (industryKpi && !industryKpiDismissed) {
    items.push(
      <ContextStackItem
        key="industry-kpi"
        tone="indigo"
        icon={TrendingDown}
        eyebrow="Industry KPI gap · from Industry Benchmarks"
        headline={`Closing the gap on ${industryKpi.kpiName}`}
        description="You arrived here from the Navigation Advantage card. The KPI has been pre-selected — review the suggested interventions and model the ROI of closing the gap."
        testId="sf-industry-kpi-banner"
        dismissTestId="sf-industry-kpi-banner-dismiss"
        dataAttrs={{ 'data-industry-kpi-id': industryKpi.kpiId }}
        onDismiss={() => {
          markDismissed('industry_kpi', industryKpiHash);
          bump();
        }}
      />,
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="sf-context-stack">
      {items}
    </div>
  );
}
